import {
  deletePushSubscription,
  savePushSubscription,
} from '@/lib/api/push-subscriptions.js';
import { isRunningInTeams } from '@/lib/teams-env.js';
import {
  isAppleMobile,
  isStandaloneDisplay,
} from '@/features/pwa-install/install-promotion.js';

/**
 * Whether this browser gets reply notifications: on, off, blocked in the browser's settings, or
 * not known yet while the app's service worker installs.
 */
export type PushState = 'on' | 'off' | 'blocked' | 'pending';

/**
 * How long a push step waits for the app's service worker. On a phone the first install caches the
 * whole app and can take minutes; a step that outlasts this gives up instead of hanging the bell.
 */
export const WORKER_WAIT_MS = 10_000;

/** The app's service worker is not active yet, so nothing can subscribe or read a subscription. */
export class WorkerNotReadyError extends Error {
  constructor() {
    super('The service worker is not active yet');
    this.name = 'WorkerNotReadyError';
  }
}

/** The window event that tells the bell its state changed elsewhere, such as from the reply prompt. */
export const PUSH_STATE_CHANGED = 'ct:push-state-changed';

/** Tells the bell to read its state again. */
export function announcePushState(): void {
  window.dispatchEvent(new Event(PUSH_STATE_CHANGED));
}

/** What decides whether the bell may offer web push here. */
export interface PushEnvironment {
  /** The app's service worker runs here; the Vite dev server registers none (`sw/register.ts`). */
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  hasNotifications: boolean;
  /** A Teams tab already gets the Teams bell, and its frame or webview cannot subscribe. */
  inTeams: boolean;
  /** iPhone and iPad allow web push only in an app added to the Home Screen. */
  appleMobile: boolean;
  standalone: boolean;
}

/** Whether the bell offers web push on this surface (MEDA-1208). */
export function pushAvailable(environment: PushEnvironment): boolean {
  if (environment.inTeams) return false;
  if (environment.appleMobile && !environment.standalone) return false;
  return (
    environment.hasServiceWorker &&
    environment.hasPushManager &&
    environment.hasNotifications
  );
}

/** Whether this browser, here and now, can get reply notifications. */
export function pushAvailableHere(): boolean {
  return pushAvailable({
    hasServiceWorker: 'serviceWorker' in navigator && !import.meta.env.DEV,
    hasPushManager: 'PushManager' in window,
    hasNotifications: 'Notification' in window,
    inTeams: isRunningInTeams(),
    appleMobile: isAppleMobile(navigator.userAgent, navigator.maxTouchPoints),
    standalone: isStandaloneDisplay(
      (query) => window.matchMedia?.(query).matches ?? false,
      (navigator as Navigator & { standalone?: boolean }).standalone,
    ),
  });
}

/**
 * The push worker (`public/push/sw.js`): registered apart from the app's Workbox worker, with its
 * own scope. It caches nothing, so it is active within a second, while the app worker's first install
 * downloads the whole app and can take minutes on a phone. Subscriptions belong to it.
 */
export const PUSH_WORKER_URL = '/push/sw.js';

/** The push worker's scope; no page of the app lives under it. */
export const PUSH_WORKER_SCOPE = '/push/';

/** Resolves once the registration has an active worker. */
function activated(registration: ServiceWorkerRegistration): Promise<void> {
  if (registration.active) return Promise.resolve();
  const worker = registration.installing ?? registration.waiting;
  return new Promise((resolve) => {
    worker?.addEventListener('statechange', () => {
      if (worker.state === 'activated') resolve();
    });
  });
}

/** The push worker's registration, registered on first use and active. */
async function pushRegistration(): Promise<ServiceWorkerRegistration> {
  const registration = await navigator.serviceWorker.register(PUSH_WORKER_URL, {
    scope: PUSH_WORKER_SCOPE,
  });
  await activated(registration);
  return registration;
}

/** The push worker's push manager, or WorkerNotReadyError when it does not activate in time. */
async function pushManager(): Promise<PushManager> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const gaveUp = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new WorkerNotReadyError()), WORKER_WAIT_MS);
  });
  try {
    return (await Promise.race([pushRegistration(), gaveUp])).pushManager;
  } finally {
    clearTimeout(timer);
  }
}

/** Resolves once the push worker is active, however long that takes. */
export async function whenWorkerReady(): Promise<void> {
  await pushRegistration();
}

/** A subscription the app worker still holds from before the push worker, or null. */
async function appWorkerSubscription(): Promise<PushSubscription | null> {
  const appRegistration = await navigator.serviceWorker.getRegistration('/');
  return (await appRegistration?.pushManager.getSubscription()) ?? null;
}

/**
 * Moves a subscription the app worker holds to the push worker: the first version subscribed
 * through the app worker, before the push worker existed. The replacement is made and stored first,
 * and only then is the old one dropped, on the server and in the browser, so a failure keeps the
 * working subscription and the next load tries again.
 * shortcut: needed only until every early subscriber has opened the app once; remove it, and
 * `push-sw.js` from the app worker's `importScripts`, after the next release has run for a while.
 */
async function moveAppWorkerSubscription(publicKey: string): Promise<void> {
  const old = await appWorkerSubscription();
  if (!old) return;
  await store(await subscriptionFor(publicKey));
  await deletePushSubscription(old.endpoint).catch(() => undefined);
  await old.unsubscribe().catch(() => false);
}

/** Turns the VAPID public key from base64url into the bytes `subscribe` takes. */
function keyBytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const base64 = (base64Url + '='.repeat((4 - (base64Url.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  return Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
}

/**
 * This browser's state: blocked by its settings, or on while it holds a subscription, in the push
 * worker or, until it has moved, in the app worker, which still receives pushes.
 */
export async function readPushState(): Promise<PushState> {
  if (Notification.permission === 'denied') return 'blocked';
  if (Notification.permission !== 'granted') return 'off';
  const current = await (await pushManager()).getSubscription();
  return current || (await appWorkerSubscription()) ? 'on' : 'off';
}

/** Whether the subscription was made with this VAPID key; pushes signed with another never reach it. */
function madeWithKey(subscription: PushSubscription, key: Uint8Array): boolean {
  const used = subscription.options.applicationServerKey;
  if (!used || used.byteLength !== key.length) return false;
  const usedBytes = new Uint8Array(used);
  return key.every((byte, index) => byte === usedBytes[index]);
}

/**
 * This browser's subscription for the server's current key: the existing one, or a new one when
 * there is none or it was made with an older key, which is dropped.
 */
async function subscriptionFor(publicKey: string): Promise<PushSubscription> {
  const manager = await pushManager();
  const key = keyBytes(publicKey);
  const existing = await manager.getSubscription();
  if (existing && madeWithKey(existing, key)) return existing;
  await existing?.unsubscribe();
  return manager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key,
  });
}

/** Stores the subscription on the server, or drops it in the browser so both stay in step. */
async function store(subscription: PushSubscription): Promise<void> {
  try {
    await savePushSubscription(subscription.toJSON());
  } catch (error) {
    await subscription.unsubscribe().catch(() => false);
    throw error;
  }
}

/**
 * Asks for permission from the person's click, subscribes with the server's key and stores the
 * subscription. A declined prompt leaves nothing subscribed.
 */
export async function turnPushOn(publicKey: string): Promise<PushState> {
  const permission = await Notification.requestPermission();
  if (permission === 'denied') return 'blocked';
  if (permission !== 'granted') return 'off';
  await store(await subscriptionFor(publicKey));
  return 'on';
}

/**
 * The bell's state on load: the browser's state, with a subscription stored again when it is on,
 * or pending while the service worker installs. A subscription the app worker still holds moves to
 * the push worker first, without asking for permission again. The state is read again after the resync, which
 * may have replaced or dropped the subscription.
 */
export async function loadPushState(publicKey: string): Promise<PushState> {
  try {
    if (Notification.permission === 'granted')
      await moveAppWorkerSubscription(publicKey).catch((error: unknown) => {
        if (error instanceof WorkerNotReadyError) throw error;
      });
    const current = await readPushState();
    if (current !== 'on') return current;
    await resyncPushSubscription(publicKey).catch(() => undefined);
    return await readPushState();
  } catch (error) {
    if (error instanceof WorkerNotReadyError) return 'pending';
    throw error;
  }
}

/** Removes a subscription on the server first, then in the browser. */
async function drop(subscription: PushSubscription | null): Promise<void> {
  if (!subscription) return;
  await deletePushSubscription(subscription.endpoint);
  await subscription.unsubscribe();
}

/** Removes this browser's subscriptions, the push worker's and one the app worker still holds. */
export async function turnPushOff(): Promise<void> {
  await drop(await (await pushManager()).getSubscription());
  await drop(await appWorkerSubscription());
}

/**
 * Stores this browser's existing subscription again: the server may have lost it, or another
 * person may have signed in on this browser, and the endpoint then follows the current user. One
 * made with an older server key is replaced first. A failed store keeps a subscription the server
 * most likely holds already, for the next load to retry, and drops a replacement it never held.
 * shortcut: a resync in one tab can subscribe again while another tab turns the bell off; add a
 * cross-tab lock if people report notifications they turned off.
 */
export async function resyncPushSubscription(publicKey: string): Promise<void> {
  if (Notification.permission !== 'granted') return;
  const existing = await (await pushManager()).getSubscription();
  if (!existing) return;
  const subscription = await subscriptionFor(publicKey);
  if (subscription.endpoint === existing.endpoint)
    await savePushSubscription(subscription.toJSON());
  else await store(subscription);
}
